import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CheckIcon from '@mui/icons-material/Check';
import { useMusicPlayer } from '../../contexts/MusicPlayerContext';
import { QUALITY_LABELS, QUALITY_DESCRIPTIONS } from '../../contexts/useStreamingQuality';

const QUALITY_ORDER = ['high', 'medium', 'low'];

// Small label in the player showing the current streaming quality
// ("320k"), opening a menu to change it - so people can see quality is
// adjustable without digging through account settings.
export const QualityMenuButton = ({ sx }) => {
  const { streamingQuality, changeStreamingQuality } = useMusicPlayer();
  const [anchorEl, setAnchorEl] = useState(null);

  const choose = (quality) => {
    setAnchorEl(null);
    changeStreamingQuality(quality);
  };

  return (
    <>
      <Button
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={`Streaming quality: ${QUALITY_LABELS[streamingQuality]}. Change quality`}
        sx={{
          minWidth: 0,
          px: 1,
          py: 0.25,
          fontSize: '0.7rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
          textTransform: 'none',
          color: 'text.secondary',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          '&:hover': { color: 'text.primary', borderColor: 'text.secondary' },
          ...sx,
        }}
      >
        {QUALITY_LABELS[streamingQuality]}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {QUALITY_ORDER.map((quality) => (
          <MenuItem key={quality} selected={quality === streamingQuality} onClick={() => choose(quality)}>
            <ListItemIcon>{quality === streamingQuality && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText primary={QUALITY_LABELS[quality]} secondary={QUALITY_DESCRIPTIONS[quality]} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// One-time prompt offering a lower quality when playback keeps stalling
// (or the browser reports a slow/data-saver connection). Never switches
// on its own - "Keep" dismisses it for the rest of the session.
export const QualitySuggestion = () => {
  const { qualitySuggestion, changeStreamingQuality, dismissQualitySuggestion } = useMusicPlayer();
  if (!qualitySuggestion) return null;

  const label = QUALITY_LABELS[qualitySuggestion.quality];
  const message = qualitySuggestion.reason === 'connection'
    ? `Your connection looks slow or data-limited. Stream at ${label} instead?`
    : `Playback keeps pausing to load. Switch to ${label}?`;

  return (
    <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ bottom: { xs: 140, md: 110 } }}>
      <Alert
        severity="info"
        variant="filled"
        sx={{ alignItems: 'center' }}
        action={
          <>
            <Button color="inherit" size="small" onClick={() => changeStreamingQuality(qualitySuggestion.quality)}>
              Switch
            </Button>
            <Button color="inherit" size="small" onClick={dismissQualitySuggestion}>
              Keep
            </Button>
          </>
        }
      >
        {message}
      </Alert>
    </Snackbar>
  );
};
